import {
  AirportHasUser,
  CreateGroupHasUserInput,
  CreatePermissionHasUserInput,
  DeleteGroupHasUserInput,
  DeleteLicenseInput,
  DeletePermissionHasUserInput,
  Group,
  License,
  PermissionHasUserEffectEnum,
  RunUpdateUserProfileInput,
  UpdateUserInput,
  User,
} from '../../models/gen/graphql';
import { Button, Col, Row } from 'react-bootstrap';
import {
  Datetime,
  Validation,
  createNotification,
  generateUpdateBulkPayload,
  getBulkValues,
  handleError,
  queryInput,
  runQueryKeys,
} from '../../utils';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import createLicenseBulk, { validateLicense } from '@/api/services/users/createLicenseBulk';
import { getDiff, stringify } from '../../utils/objects';
import { refetchSearchUsers, useGetUserForModal } from '@/api/services/users/searchUsers';

import { AirportDropdownField } from '@/components/AirportDropdown';
import { DATE_FE_FORMAT_FULL } from '../../constants';
import EditModal from '../EditModal/new';
import FormField from '../FormField';
import Logger from '../../utils/logs';
import PasswordInput from '../PasswordInput';
import PhoneInput from '../MaskedInput/PhoneInput';
import ProfileHeader from './ProfileHeader';
import SelectActiveStatus from '../SelectActiveStatus';
import SelectAirportGroupPermission from '../SelectAirportGroupPermission';
import SelectCompany from '../SelectCompany';
import SelectGroup from '../SelectGroup';
import SelectLicenseType from '../SelectLicenseType';
import SelectRole from '../SelectRole';
import SelectState from '../SelectState';
import { Toast } from '../../models';
import createGroupHasUserBulk from '@/api/services/users/createGroupHasUserBulk';
import createPermissionHasUserBulk from '@/api/services/users/createPermissionHasUserBulk';
import createUserBulk from '@/api/services/users/createUserBulk';
import updateUserProfileBulk from '@/api/services/users/updateUserProfileBulk';
import useForm from '../../hooks/useForm';
import useSession from '@/state/session';
import useUuid from '../../hooks/useUuid';

const log = Logger.of('EditUsersModal');

interface Props {
  show: boolean;
  title?: string;
  onHide?: Function;
  onSubmit?: (update: any, original: User[]) => void;
  onExited?: Function;
  onCancel?: Function;
  data: any;
  options?: {
    selected?: string[]; // array of user ids will be used to query or default to data.id
  };
  loading?: boolean;
}

type EditUserModalState = {
  loading: boolean;
  isUsernameTaken?: boolean;
  requiredLoading?: boolean; // will be used to disable submitting when required fields are validating asynchronously
};
const initEditUserModalState: EditUserModalState = {
  loading: false,
  isUsernameTaken: false,
  requiredLoading: false,
};

const EditUsersModal = ({ show, onHide, onSubmit, onCancel, onExited, data: initData, options = {}, ...props }: Props): JSX.Element => {
  const original = useRef({ ...initData });
  const [state, setState] = useState(initEditUserModalState);
  const [form, onChange, setForm] = useForm(initData); // form data state

  const [{ data, loading: loadingSearchUsers }, { refetch: searchUser }] = useGetUserForModal();
  const user = (data || []).find((node: User): boolean => node.id === initData?.id);

  const loading = loadingSearchUsers || state?.loading;
  // Init GraphApi useUsers hook

  const driverRoleId = useSession(({ state }) => state.config?.driverRoleId || null);

  const mode = initData?.id ? 'edit' : 'create';
  const selected = data || [];
  const selectedCount = selected?.length || 0;
  const fields = useMemo((): any => (mode === 'edit' ? getBulkValues(selected) : undefined), [selectedCount, mode]);
  const uuid = useUuid();

  // Temp validation solution for v1.
  // TODO: Refactor to a global validator in the future.
  // NOTE: We should not use our existing validator utility as it has been causing performance issues.
  // A new validator will need to be scoped out and created after v1.
  const validations = useMemo(
    (): any => ({
      roleId: (val: any): boolean => Validation.isValidUUID(val),
      username: (val: any): boolean => !state.isUsernameTaken && !!val,
      password: (val: any): boolean => !!val,
      confirmPassword: (val: any, form: any): boolean => !!val?.length && val === form?.password,
      firstName: (val: any): boolean => !!val,
      lastName: (val: any): boolean => !!val,
      companyId: (val: any): boolean => Validation.isValidUUID(val),
      email: (val: any): boolean => Validation.isEmail(val),
      active: (val: any): boolean => Validation.isNumber(val),
      licenses: (val: any, form: any): boolean => {
        if (form?.roleId !== driverRoleId) return true;
        // require at least one license
        if (!val?.length) return false;
        const currentDatetime = new Datetime().toString();
        const validLicenses = val?.map?.((license: License): boolean => {
          const expirationDatetime = license?.expirationDate ? new Datetime(license?.expirationDate) : null;
          return (
            !!license?.number &&
            !!expirationDatetime &&
            (expirationDatetime.isAfter(currentDatetime, 'day') || expirationDatetime.isSame(currentDatetime, 'day')) &&
            !!license?.stateCode &&
            !!license?.licenseTypeId
          );
        });
        return !validLicenses.includes(false);
      },
      primaryAirportCode: (val: any): boolean => {
        if (form?.roleId !== driverRoleId) return true;
        return !!val;
      },
    }),
    [form?.roleId, driverRoleId, state.isUsernameTaken]
  );

  const handleSubmit = async (): Promise<void> => {
    const title = mode === 'edit' ? 'Update User' : 'Create User';
    try {
      setState({ loading: true });
      if (mode === 'edit') await handleUpdateUserBulk(form, selected);
      else await handleCreateUserBulk(form);
      if (onSubmit) onSubmit(form, selected);
      createNotification('Success', Toast.Type.SUCCESS, title);
      onHide();
    } catch (err) {
      handleError(err, { notification: { title } });
    }
  };

  const handleSearch = async (): Promise<void> => {
    try {
      if (!initData?.id || !show) return;
      const res = await searchUser([{ id: queryInput((options.selected || []).length ? options.selected : initData?.id) }], {
        pageSize: (options.selected || []).length || 1,
      });
      const user: User = res?.find((node: User): boolean => node.id === initData?.id);
      if (!user) return createNotification('User not found.', Toast.Type.WARNING, 'Search User');
      original.current = { ...user };
      setForm({ ...user });
    } catch (err) {
      log.error(err);
    }
  };

  useEffect((): void => {
    if (show) {
      handleSearch();
    } else {
      setState(initEditUserModalState);
      setForm(undefined);
    }
  }, [show]);

  // Set State Handlers
  const handleAddLicense = (): void => {
    setForm((current: any): any => ({
      ...current,
      licenses: [
        ...(current.licenses || []),
        {
          number: '',
          stateCode: '',
          expirationDate: new Datetime().toString(),
          licenseTypeId: '',
        },
      ],
    }));
  };
  const handleRemoveLicense = (index: number): void => {
    setForm((current: any): any => ({
      ...current,
      licenses: current.licenses.filter((license: License, a: number): boolean => a !== index),
    }));
  };

  const handleChange = (name: string) => (val) => {
    setForm((current) => ({
      ...current,
      [name]: val,
    }));
  };

  const onTrimString = (event: any): any => onChange({ target: { name: event.target.name, value: event.target.value.trim() } });

  const title =
    props?.title || mode === 'edit' ? `${user?.firstName || 'unknown'} ${user?.lastName || ''}`.toUpperCase() : 'Create New User';
  const valid = useMemo((): { [key: string]: boolean } => {
    const result = {};
    Object.keys(validations)
      .filter((key: string): boolean => mode === 'create' || !key.toLowerCase().includes('password'))
      .forEach((key: string): void => {
        result[key] = validations[key]?.(form?.[key], form);
      });
    return result;
  }, [form, validations]);

  return (
    <EditModal
      show={show}
      title={title}
      icon="fa fa-location-dot"
      size="xl"
      onHide={onHide}
      onCancel={onCancel}
      onExited={onExited}
      onSubmit={Object.values(valid).includes(false) || stringify.compare(original.current || {}, form || {}) ? false : handleSubmit}
      options={{ Header: mode === 'edit' ? <ProfileHeader data={user || initData} /> : undefined }}
      loading={props?.loading || loading}
      loadingValidation={state.requiredLoading}
      name={'editUsers'}
      changes={!stringify.compare(original.current || {}, form || {})}
    >
      {/* Form Inputs */}
      <Row className="d-flex justify-content-center">
        <Col xs={12} lg={6}>
          {/* Left Section */}
          <FormField
            label="Type:"
            name="roleId"
            value={form?.roleId || ''}
            // TODO: Disable this field if the user does not have permission to edit roles
            disabled={(fields?.roleId || []).length > 1}
            onChange={onChange}
            placeholder="--"
            valid={Validation.isValidUUID(form?.roleId)}
            searchable
            required
            options={{
              input: {
                as: SelectRole,
              },
            }}
          />
          <FormField
            label="Username:"
            name="username"
            value={form?.username || ''}
            disabled={(fields?.username || []).length > 1}
            onChange={onTrimString}
            valid={!state.isUsernameTaken && !!form?.username}
            onBlur={({ target: { value } }) => {
              if (!value) return;
              if (value === original.current?.username) {
                setState((current) => ({ ...current, isUsernameTaken: false }));
                return;
              }
              setState((current) => ({ ...current, requiredLoading: true }));
              refetchSearchUsers([{ username: queryInput(value) }], {
                pageSize: 1,
              })
                .then((res) => {
                  setState((current) => ({
                    ...current,
                    isUsernameTaken: (res?.rows || []).length > 0,
                  }));
                })
                .catch((err) => log.error('Error validating username availability:', err))
                .finally(() => setState((current) => ({ ...current, requiredLoading: false })));
            }}
            required
            feedback={state.isUsernameTaken ? 'Username is already taken.' : undefined}
          />
          {/* // Display password field when creating a user */}
          {mode === 'create' && (
            <>
              <PasswordInput
                label="Password:"
                name="password"
                type="password"
                value={form?.password || ''}
                onChange={onChange}
                valid={!!form?.password}
                required
              />
              <PasswordInput
                label="Confirm Password:"
                name="confirmPassword"
                type="password"
                value={form?.confirmPassword || ''}
                onChange={onChange}
                valid={!!form?.confirmPassword?.length && form?.password === form?.confirmPassword}
                required
              />
            </>
          )}
          <FormField
            label="Firstname:"
            name="firstName"
            value={form?.firstName || ''}
            disabled={(fields?.firstName || []).length > 1}
            onChange={onTrimString}
            required
            valid={!!form?.firstName}
          />
          <FormField
            label="Lastname:"
            name="lastName"
            value={form?.lastName || ''}
            disabled={(fields?.lastName || []).length > 1}
            required
            valid={!!form?.lastName}
            onChange={onTrimString}
          />
          <FormField
            label="Company:"
            name="companyId"
            value={form?.companyId || ''}
            // TODO: Disable this field if the user does not have permission to edit roles
            disabled={(fields?.companyId || []).length > 1}
            onChange={onChange}
            placeholder="--"
            valid={Validation.isValidUUID(form?.companyId)}
            searchable
            options={{
              input: {
                as: SelectCompany,
              },
            }}
          />
          <FormField
            label="Full Address:"
            name="address"
            value={form?.address || ''}
            disabled={(fields?.address || []).length > 1}
            onChange={onChange}
          />
          <FormField
            label="City:"
            name="cityName"
            value={form?.cityName || ''}
            disabled={(fields?.cityName || []).length > 1}
            onChange={onChange}
          />
          <FormField
            label="State:"
            name="stateCode"
            value={form?.stateCode || ''}
            disabled={(fields?.stateCode || []).length > 1}
            onChange={onChange}
            placeholder="--"
            searchable
            options={{
              input: {
                as: SelectState,
              },
            }}
          />
          <FormField
            label="Zip:"
            type="number"
            name="zipCode"
            min="0"
            max="9999999999"
            value={form?.zipCode || ''}
            disabled={(fields?.zipCode || []).length > 1}
            onChange={onChange}
          />
          <FormField
            label="Medical Info:"
            type="textarea"
            name="notes"
            value={form?.notes || ''}
            onChange={onChange}
            maxLength={250}
            options={{
              input: {
                className: '{height:8rem;min-height:8rem!;}',
              },
            }}
          />
        </Col>
        {/* Right Section */}
        <Col xs={12} lg={6}>
          <FormField
            label="Employee ID:"
            name="employeeId"
            value={form?.employeeId || ''}
            disabled={(fields?.employeeId || []).length > 1}
            onChange={onTrimString}
            maxLength={16}
          />
          <FormField
            label="Email:"
            name="email"
            value={form?.email || ''}
            disabled={(fields?.email || []).length > 1}
            onChange={onTrimString}
            valid={Validation.isEmail(form?.email)}
            required
          />
          <FormField
            label="Phone:"
            name="phone"
            value={form?.phone || ''}
            disabled={(fields?.phone || []).length > 1}
            onChange={(value) => onChange({ target: { name: 'phone', value } })}
            options={{
              input: {
                as: PhoneInput,
              },
            }}
          />
          <FormField
            label="Assigned Airports:"
            name="airportCodes"
            value={(form?.airportCodes || []).map((node) => node?.permissionId || node)}
            disabled={(fields?.airportCodes || []).length > 1}
            onChange={onChange}
            placeholder="--"
            options={{
              input: {
                as: (props) => <SelectAirportGroupPermission {...props} multiple searchable />,
              },
            }}
          />
          <AirportDropdownField
            label="Primary Airport:"
            placeholder="Airport"
            value={form?.primaryAirportCode}
            onChange={handleChange('primaryAirportCode')}
            options={{ locale: { 'Select...': 'Airport' } }}
            isValid={form?.primaryAirportCode}
            isInvalid={form?.roleId === driverRoleId ? !form?.primaryAirportCode : undefined}
          />
          <FormField
            label="Assigned Roles:"
            name="groups"
            value={(form?.groups || []).map((el: any): any => el.id || el)}
            disabled={(fields?.groups || []).length > 1}
            onChange={onChange}
            placeholder="--"
            multiple
            searchable
            options={{
              input: {
                as: SelectGroup,
              },
            }}
          />
          <FormField
            label="Birth Date:"
            type="date"
            name="dob"
            value={form?.dob ? new Datetime(form?.dob).dateInput : ''}
            onChange={onChange.date}
            format={DATE_FE_FORMAT_FULL}
          />
          <FormField
            label="Hire Date:"
            type="date"
            name="startDate"
            value={form?.startDate ? new Datetime(form?.startDate).dateInput : ''}
            onChange={onChange.date}
            format={DATE_FE_FORMAT_FULL}
          />
          <FormField
            label="Status:"
            name="active"
            value={form?.active}
            disabled={(fields?.active || []).length > 1}
            onChange={onChange.int}
            placeholder="--"
            valid={Validation.isNumber(form?.active)}
            options={{
              input: {
                as: SelectActiveStatus,
              },
            }}
          />
          {form?.roleId === driverRoleId && (
            <FormField
              label="Long Trip?"
              id={`longTrip_${uuid}`}
              type="switch"
              name="longTrip"
              checked={!!form?.longTrip}
              onChange={onChange.toggleInt}
              options={{
                group: {
                  className: 'd-flex align-items-center gap-2',
                },
              }}
            />
          )}
        </Col>
      </Row>
      {form?.roleId === driverRoleId && selectedCount <= 1 && (
        <Row>
          <Col>
            <h3 className="{border-bottom:1px|solid|grey;padding:.7rem|0;margin-bottom:1.5rem!;}">Licenses</h3>
            <div className="{overflow:auto;overflow-x:hidden;max-height:180px;}">
              {(form?.licenses || []).map(
                (license: License, idx: number): JSX.Element => (
                  <UserLicense
                    number={license?.number}
                    expirationDate={license?.expirationDate}
                    stateCode={license?.stateCode}
                    licenseTypeId={license?.licenseTypeId}
                    onChange={onChange}
                    onRemoveLicense={(): void => handleRemoveLicense(idx)}
                    idx={idx}
                    key={idx}
                  />
                )
              )}
              <UserLicense onAddLicense={handleAddLicense} />
            </div>
          </Col>
        </Row>
      )}
    </EditModal>
  );
};

const UserLicense = ({
  number = '',
  expirationDate = '',
  stateCode = '',
  licenseTypeId = '',
  idx,
  onChange,
  onAddLicense,
  onRemoveLicense,
}: {
  number?: string;
  expirationDate?: string;
  stateCode?: string;
  licenseTypeId?: string;
  idx?: number;
  onChange?: any;
  onAddLicense?: any;
  onRemoveLicense?: any;
}): JSX.Element => {
  const currentDatetime = new Datetime().toString();
  const expirationDatetime = expirationDate ? new Datetime(expirationDate) : null;
  const validNumber = !!number;
  const validExpirationDate =
    !!expirationDatetime && (expirationDatetime?.isAfter(currentDatetime, 'day') || expirationDatetime?.isSame(currentDatetime, 'day'));
  const validStateCode = !!stateCode;
  const validLicenseTypeId = !!licenseTypeId;

  return (
    <Row key={idx}>
      <Col className="d-flex justify-content-space-between gap-4 mb-2">
        {!!onRemoveLicense && (
          <FormField
            label="License Number:"
            name={`licenses.${idx}.number`}
            value={number}
            onChange={onChange}
            valid={validNumber}
            condensed
          />
        )}
        {!!onAddLicense && (
          <div className="w-100 mt-3">
            <Button className="d-block w-100 {white-space:nowrap!;}" variant="outline-success" onClick={onAddLicense}>
              <i className="sv sv-plus-square {font-size:1.25rem;}" />
              <span>Add License</span>
            </Button>
          </div>
        )}
        <FormField
          type="date"
          label="Exp Date:"
          name={`licenses.${idx}.expirationDate`}
          value={new Datetime(expirationDate || undefined).dateInput}
          valid={validExpirationDate}
          onChange={onChange?.date}
          condensed
          options={{
            group: {
              className: `{visibility:${!onRemoveLicense ? 'hidden' : 'visible'}!;}`,
            },
          }}
          format={DATE_FE_FORMAT_FULL}
        />
        <FormField
          name={`licenses.${idx}.stateCode`}
          value={stateCode}
          onChange={onChange}
          label="State:"
          valid={validStateCode}
          searchable
          options={{
            group: {
              className: `{visibility:${!onRemoveLicense ? 'hidden' : 'visible'}!;}`,
            },
            input: {
              as: SelectState,
            },
          }}
          condensed
        />
        <FormField
          name={`licenses.${idx}.licenseTypeId`}
          value={licenseTypeId}
          onChange={onChange}
          label="License Type:"
          valid={validLicenseTypeId}
          searchable
          options={{
            group: {
              className: `{visibility:${!onRemoveLicense ? 'hidden' : 'visible'}!;}`,
            },
            input: {
              as: SelectLicenseType,
            },
          }}
          condensed
        />
        <div className="d-flex flex-column justify-content-center {margin-top:0.9rem;}">
          <Button
            className="rounded {transform:scale(0.75);}"
            variant={onRemoveLicense ? 'outline-danger' : 'outline-success'}
            size="sm"
            onClick={onRemoveLicense || onAddLicense}
            style={{
              visibility: !onRemoveLicense ? 'hidden' : 'visible',
            }}
          >
            <i className={`sv sv-${onRemoveLicense ? 'minus' : 'plus'}`} />
          </Button>
        </div>
      </Col>
    </Row>
  );
};

interface UserUpdate extends User {
  removedLicenses: License[];
}
interface UserCreate extends User {
  confirmPassword: string;
}

const handleCreateUserBulk = async (create: UserCreate): Promise<any> => {
  const { licenses = [], airportCodes: permissions = [], groups = [], confirmPassword = '' } = create;

  // Validate User Inputs
  const invalid = [];
  if ((create.username || '') === '') invalid.push('username');
  if (create.password !== confirmPassword || create.password === '') invalid.push('password');
  if ((create.firstName || '') === '') invalid.push('firstname');
  if ((create.lastName || '') === '') invalid.push('lastname');
  if (!Validation.isEmail(create.email)) invalid.push('email');
  if (!Validation.isValidUUID(create.roleId)) invalid.push('role');
  if (!Validation.isValidUUID(create.companyId)) invalid.push('company');
  if (isNaN(create.active)) invalid.push('status');
  if (invalid.length > 0) {
    createNotification(`Invalid fields: ${invalid.sort().join(', ')}`, Toast.Type.DANGER, 'Create User');
    return;
  }

  const res = await createUserBulk([create]);

  for (let r = 0; r < res.length; r++) {
    const { error, node: { id = '' } = {} } = res[r];

    if (!Validation.isNil(error)) throw new Error(error);

    // Create License
    if (licenses.length > 0) {
      const createLicensePayload = licenses.map((node: License): License => ({ ...node, userId: id }));
      await createLicenseBulk(createLicensePayload);
    }

    // Create Permission Has User
    if (permissions.length > 0) {
      const createPermissionHasUserPayload: CreatePermissionHasUserInput[] = permissions.map(
        (permission: any): CreatePermissionHasUserInput => ({
          permissionId: permission?.id || permission,
          userId: id,
          effect: PermissionHasUserEffectEnum.Allow,
        })
      );
      await createPermissionHasUserBulk(createPermissionHasUserPayload);
    }

    // Create Group Has User
    if (groups.length > 0) {
      const createGroupHasUserPayload: CreateGroupHasUserInput[] = groups.map(
        (group: any): CreateGroupHasUserInput => ({ groupId: group?.id || group, userId: id })
      );
      await createGroupHasUserBulk(createGroupHasUserPayload);
    }
  }
  return res;
};

const handleUpdateUserBulk = async (
  { groups = [], airportCodes: permissions = [], licenses = [], ...update }: UserUpdate,
  selected: User[]
): Promise<any> => {
  if (!(selected || [])?.length) throw new Error('handleUpdateUserBulk: 0 rows selected');
  if (Object.values(update || {}).length === 0) throw new Error('handleUpdateUserBulk: update contains 0 values');

  //Get user updates
  const {
    groups: originalGroups,
    airportCodes: originalPermissions,
    licenses: originalLicenses,
    ...original
  } = (selected || [])?.find((node: User): boolean => node.id === update?.id) || {};

  if (!Object.values(original || {}).length) throw new Error('handleUpdateUserBulk: original contains 0 values');

  const [{ avatar: _avatar, ...partial }] = getDiff(original, update); // getdiff 0 idx returns the update partial

  const bundle = (selected || []).map((node: User): User => ({ ...partial, id: node?.id }));
  const userUpdates = generateUpdateBulkPayload(bundle);

  // Format relational user data
  // Update License
  const updateLicenses = (licenses || [])
    .filter((node: License): boolean => !!node.id)
    .filter((node: License): boolean => {
      const originalLicenseTemp = originalLicenses.find((el: License): boolean => el.id === node.id);
      const temp = runQueryKeys(validateLicense.keys, getDiff(originalLicenseTemp, node)[0]);
      return Object.values(temp || {}).length > 0;
    })
    .map((node: License): License => {
      const originalLicenseTemp = originalLicenses.find((el: License): boolean => el.id === node.id);
      const temp = runQueryKeys(validateLicense.keys, getDiff(originalLicenseTemp, node)[0]);
      return { ...temp, id: node.id };
    });

  // Create License
  const createLicenses = (licenses || []).filter((node: License): boolean => !node.id);

  // Delete License
  const deleteLicenses = (originalLicenses || [])
    .filter((node: License): boolean => !(licenses || []).filter((el: any): boolean => el?.id === node?.id).length)
    .map(({ id }: License): DeleteLicenseInput => ({ id }));

  const permissionIdOf = (node: any): string => node?.permissionId || node?.id || node;

  // Create permissionHasUser
  const createPermissionHasUser = (permissions || []).filter(
    (node: any): boolean =>
      !(originalPermissions || []).filter((el: AirportHasUser): boolean => permissionIdOf(el) === permissionIdOf(node)).length
  );

  // Delete permissionHasUser
  const deletePermissionHasUser = (originalPermissions || []).filter(
    (node: AirportHasUser): boolean => !(permissions || []).filter((el: any): boolean => permissionIdOf(el) === permissionIdOf(node)).length
  );

  // Create GroupHasUser
  const createGroupHasUser = (groups || []).filter(
    (node: any): boolean => !(originalGroups || []).filter((el: Group): boolean => (el?.id || el) === (node?.id || node)).length
  );

  // Delete GroupHasUser
  const deleteGroupHasUser = (originalGroups || []).filter(
    (node: Group): boolean => !(groups || []).filter((el: any): boolean => (el?.id || el) === (node?.id || node)).length
  );

  // Format update payload
  const updateUserProfileInputs: RunUpdateUserProfileInput[] = (userUpdates || []).map(
    ({ value, ...node }: UpdateUserInput, idx: number): RunUpdateUserProfileInput => {
      const query = (node.query || [])[0] || {};
      const output: RunUpdateUserProfileInput = {
        ...node,
      };

      output.createPermissionHasUserBulk = [];
      output.deletePermissionHasUserBulk = [];
      output.createGroupHasUserBulk = [];
      output.deleteGroupHasUserBulk = [];

      (query.id?.[0].values || []).forEach((userId: string): void => {
        // we only want to create license if we are updating one user
        if (idx === 0 && (selected || [])?.length === 1) {
          output.createLicenseBulk = createLicenses.map((node: License): License => ({ ...node, userId }));
          output.updateLicenseBulk = generateUpdateBulkPayload(updateLicenses);
          output.deleteLicenseBulk = deleteLicenses;
        }

        output.createPermissionHasUserBulk = [
          ...output.createPermissionHasUserBulk,
          ...createPermissionHasUser.map(
            (el: any): CreatePermissionHasUserInput => ({
              permissionId: permissionIdOf(el),
              userId,
              effect: PermissionHasUserEffectEnum.Allow,
            })
          ),
        ];
        output.deletePermissionHasUserBulk = [
          ...output.deletePermissionHasUserBulk,
          ...deletePermissionHasUser.map((el: AirportHasUser): DeletePermissionHasUserInput => ({ permissionId: el.permissionId, userId })),
        ];
        output.createGroupHasUserBulk = [
          ...output.createGroupHasUserBulk,
          ...createGroupHasUser.map((el: any): CreateGroupHasUserInput => ({ groupId: el, userId })),
        ];
        output.deleteGroupHasUserBulk = [
          ...output.deleteGroupHasUserBulk,
          ...deleteGroupHasUser.map((el: Group): DeleteGroupHasUserInput => ({ groupId: el.id, userId })),
        ];
      });

      return {
        ...output,
        updateUserValues: value,
      };
    }
  );

  const noChangesMade = Object.entries(updateUserProfileInputs?.[0] || {})
    .filter(([key]): boolean => key.toLowerCase() !== 'query')
    .every(([, value]): boolean => (Array.isArray(value) ? !value?.length : !Object.keys(value)?.length));

  if (noChangesMade) return createNotification('No changes made', Toast.Type.WARNING, 'Update User Profile');

  // exec update
  return await updateUserProfileBulk(updateUserProfileInputs);
};

export default React.memo(EditUsersModal);
